import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Types
export interface ChatRoom {
  id: string;
  buildingId: string;
  isActive: boolean;
  isLocked?: boolean;
  lockedBy?: string;
  lockedAt?: string;
  createdAt: string;
  updatedAt: string;
  participants: ChatParticipant[];
  messages: Message[];
}

export interface ChatParticipant {
  id: string;
  chatId: string;
  userId?: string;
  apartmentId?: string;
  isActive: boolean;
  joinedAt: string;
  leftAt?: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  apartment?: {
    id: string;
    code: string;
    ownerName: string;
    building?: {
      name: string;
    };
  };
}

export interface Message {
  id: string;
  chatId: string;
  senderId: string;
  senderType: 'USER' | 'RESIDENT';
  content: string;
  messageType: 'TEXT' | 'IMAGE' | 'FILE';
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
  sender: {
    id: string;
    name: string;
  };
}

export interface CreateRoomRequest {
  buildingId: string;
  participantIds: string[];
  roomName?: string;
}

// API functions
export const chatApi = {
  // Get user's chat rooms
  getRooms: async (): Promise<ChatRoom[]> => {
    const response = await axios.get(`${API_URL}/chat/rooms`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get specific chat room
  getRoom: async (roomId: string): Promise<ChatRoom> => {
    const response = await axios.get(`${API_URL}/chat/rooms/${roomId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get message history
  getMessages: async (
    roomId: string,
    limit: number = 50,
    before?: string
  ): Promise<Message[]> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (before) params.append('before', before);

    const response = await axios.get(
      `${API_URL}/chat/rooms/${roomId}/messages?${params}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Create new chat room
  createRoom: async (data: CreateRoomRequest): Promise<ChatRoom> => {
    const response = await axios.post(`${API_URL}/chat/rooms`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Send message via REST (fallback)
  sendMessage: async (roomId: string, content: string): Promise<Message> => {
    const response = await axios.post(
      `${API_URL}/chat/rooms/${roomId}/messages`,
      { content },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Add participant to room
  addParticipant: async (roomId: string, userId: string): Promise<ChatParticipant> => {
    const response = await axios.post(
      `${API_URL}/chat/rooms/${roomId}/participants`,
      { userId },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Remove participant from room
  removeParticipant: async (roomId: string, userId: string): Promise<void> => {
    await axios.post(
      `${API_URL}/chat/rooms/${roomId}/participants/remove`,
      { userId },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
  },

  // Get or create chat room for apartment
  getOrCreateRoomForApartment: async (apartmentId: string): Promise<ChatRoom> => {
    const response = await axios.post(
      `${API_URL}/chat/apartment/${apartmentId}/room`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Send message with file attachment
  sendMessageWithFile: async (roomId: string, formData: FormData): Promise<Message> => {
    const response = await axios.post(
      `${API_URL}/chat/rooms/${roomId}/messages/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Lock chat room (staff only)
  lockRoom: async (roomId: string): Promise<ChatRoom> => {
    const response = await axios.post(
      `${API_URL}/chat/rooms/${roomId}/lock`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Unlock chat room (staff only)
  unlockRoom: async (roomId: string): Promise<ChatRoom> => {
    const response = await axios.post(
      `${API_URL}/chat/rooms/${roomId}/unlock`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Mark messages as read
  markAsRead: async (roomId: string): Promise<void> => {
    await axios.post(
      `${API_URL}/chat/rooms/${roomId}/read`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
  },
}; 